import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckIcon } from '@heroicons/react/24/outline'
import { classifyAvailability, formatPrice } from '../../utils'
import { buildModelUrl } from '../../utils/modelUrl'
import { fetchProducts } from '../../requests'
import { useTranslation } from 'react-i18next'
import { KRAJ } from '../../kraj'

/**
 * Kafelek modułu — wzorowany na ręcznych zestawieniach handlowych: zdjęcie,
 * producent, model, moc, gwarancja, cena i dostępność.
 *
 * ═══ JEDEN KOMPONENT NA DWA EKRANY ═══
 * Używają go PREFERENCJE DOBORU (podgląd slotu) i ASYSTENT (lista „Twoje
 * wybrane moduły"). Wcześniej asystent miał własną, uboższą listę i te same
 * produkty wyglądały w dwóch miejscach inaczej — a to ma być ten sam moduł,
 * więc ma wyglądać tak samo. Przy dwóch kopiach pierwsza zmiana wyglądu
 * rozjechałaby je z powrotem.
 *
 * `onWybierz` przełącza kafelek w tryb wyboru (asystent): ramka zaznaczenia
 * i przycisk przypięcia do zestawu. Bez tej właściwości kafelek jest samym
 * podglądem, tak jak w ustawieniach konta.
 *
 * Odnośnik do modelu jest ZAWSZE osobnym elementem, nigdy zagnieżdżonym
 * w przycisku wyboru — odnośnik wewnątrz przycisku to niepoprawny HTML
 * i klikając go trafiało się losowo w jedno albo drugie.
 */
export const TON_DOSTEPNOSCI = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mid: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-orange-50 text-orange-700 border-orange-200',
  order: 'bg-sky-50 text-sky-700 border-sky-200',
  out: 'bg-red-50 text-red-600 border-red-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-200',
}

const KafelekModulu = ({ oferta, polecany, wybrany = false, onWybierz = null }) => {
  const { t } = useTranslation('asystent')
  // Zdjęcie leży u sklepu i bywa nie do pobrania (pv-met.pl ma zły certyfikat SSL, pvhurt.pl ochronę przed botami).
  // Wtedy bierzemy zdjęcie modelu, a gdy i ono zawiedzie, pokazujemy „brak zdjęcia” zamiast pękniętego obrazka.
  const [zdjecie, setZdjecie] = useState(oferta.img_url)
  const [zModelu, setZModelu] = useState(false)
  useEffect(() => { setZdjecie(oferta.img_url); setZModelu(false) }, [oferta.img_url])
  const zdjecieNieWczytane = async () => {
    if (zModelu || !oferta.model_id) { setZdjecie(null); return }
    setZModelu(true)
    try {
      const model = await fetchProducts(`get_panel_model/${oferta.model_id}`)
      setZdjecie(model?.img_url && model.img_url !== zdjecie ? model.img_url : null)
    } catch {
      setZdjecie(null)
    }
  }
  const av = classifyAvailability(oferta.availability)
  const wybieralny = typeof onWybierz === 'function'

  return (
    <div className={`rounded-xl border overflow-hidden bg-white transition-colors ${
      wybieralny && wybrany ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
    }`}>
      <div className="relative h-28 bg-white flex items-center justify-center p-2 border-b border-gray-100">
        {polecany && (
          <span className="absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 rounded-md
                           bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm">
            {t('kafelek.poleca')}
          </span>
        )}
        {wybieralny && wybrany && (
          <span className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 px-1.5 py-0.5
                           rounded-md bg-blue-600 text-white text-[10px] font-bold shadow-sm">
            <CheckIcon className="w-3 h-3" />
            {t('kafelek.w_zestawie')}
          </span>
        )}
        {zdjecie
          ? <img src={zdjecie} alt={oferta.name} onError={zdjecieNieWczytane} className="max-h-full max-w-full object-contain" />
          : <span className="text-xs text-gray-300">{t('kafelek.brak_zdjecia')}</span>}
      </div>
      <div className="p-2.5 space-y-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
          {oferta.manufacturer || t('kafelek.brak_producenta')}
        </p>
        <p className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2" title={oferta.name}>
          {oferta.name}
        </p>
        <p className="text-xs text-gray-600">
          <strong>{oferta.power || '?'} Wp</strong>
          {oferta.types?.length > 0 && <> · {oferta.types.join(' / ')}</>}
        </p>
        <p className="text-xs text-gray-500">
          {oferta.product_warranty ? t('kafelek.gwarancja', { lata: oferta.product_warranty }) : ''}
          {oferta.product_warranty && oferta.price_wp ? ' · ' : ''}
          {oferta.price_wp ? `${String(oferta.price_wp.toFixed(2)).replace('.', ',')} ${KRAJ.symbol}/Wp` : ''}
        </p>
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <span className="text-sm font-bold text-gray-900">{formatPrice(oferta.price)} {KRAJ.symbol}</span>
          <span className={`px-1.5 py-0.5 rounded-md border text-[10px] font-semibold ${TON_DOSTEPNOSCI[av.tone]}`}>
            {av.label}
          </span>
        </div>

        {wybieralny && (
          <button
            type="button"
            onClick={() => onWybierz(wybrany ? null : oferta)}
            aria-pressed={wybrany}
            className={`inline-flex items-center justify-center w-full mt-1 px-2 py-1.5 rounded-lg
                        text-[11px] font-semibold transition-colors ${
              wybrany
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'border border-blue-200 text-blue-700 hover:bg-blue-50'
            }`}
          >
            {wybrany ? t('kafelek.cofnij') : t('kafelek.wybierz')}
          </button>
        )}

        {oferta.model_id && (
          <Link to={buildModelUrl('panel', { id: oferta.model_id })}
                className={`inline-flex items-center justify-center w-full mt-1 px-2 py-1.5 rounded-lg
                            text-[11px] font-semibold transition-colors no-underline ${
                  wybieralny
                    ? 'text-gray-500 hover:text-blue-600 hover:bg-gray-50'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}>
            {t('kafelek.przejdz')}
          </Link>
        )}
      </div>
    </div>
  )
}

export default KafelekModulu
